<template>
  <div class="multi-page">
    <page-header></page-header>
    <back-to-top />
    <div class="heading-button-container">
      <h1 class="page-title">
        Multi Sign Off
        <span class="project-name">
          for session: {{ projects.name }}
        </span>
      </h1>
      <div class="button-column">
        <div class="button-group bottom-buttons">
          <!-- <button class="add-task-button" @click="openAddTasksModal()">Add a Task</button> -->
          <button class="back-button" @click="goBack">Back</button>
        </div>
      </div>
    </div>
    <div class="steps-container">
      <div class="artificial-fieldset-steps">
        <div class="legend-wrapper">
          <span class="artificial-fieldset-steps-legend">Step 1: Select Group of Trainees</span>
        </div>
        <div class="trainee-step-content">
          <button @click="openSelectGroupModal" class="add-task-button" :disabled="processing === true">
            {{ processing ? "Loading..." : "Select Group" }}
          </button>
          <div v-if="selectedTrainees.length" class="chip-container">
            <span v-for="trainee in selectedTrainees" :key="trainee.user_id" class="chip"
              :class="{ 'locked-chip': isTraineeLocked(trainee.user_id) }">
              <span v-if="isTraineeLocked(trainee.user_id)" class="lock-icon" title="This trainee's logbook has been locked">🔒</span>
              {{ trainee.first_name + ' ' + trainee.last_name }}
              <font-awesome-icon class="chip-close" :icon="['fas', 'times']" title="Remove trainee from the current selection" @click="removeTrainee(trainee)" />
            </span>
          </div>
        </div>
      </div>
      <div v-if="selectedTrainees.length" class="artificial-fieldset-steps">
        <div class="legend-wrapper">
          <span class="artificial-fieldset-steps-legend">Step 2: Filter by Instructor (optional)</span>
        </div>
        <instructor-filter v-model="selectedInstructorIds" :options="instructorOptions" :current-user-id="instructorId"
          :disabled="processing" />
      </div>
    </div>
    <div class="artificial-fieldset-steps">
      <div class="legend-wrapper">
        <span class="artificial-fieldset-steps-legend">{{ selectedTrainees.length ? 'Step 3' : 'Step 2' }}: Select Tasks</span>
      </div>

      <div class="heading-button-container" v-if="selectedTrainees.length">
        <div class="artificial-fieldset" style="margin-bottom: 20px;">
          <label class="artificial-fieldset-legend">Legend</label>
          <table class="help-info-table" style="text-align: left;">
            <tbody>
              <tr>
                <td style="padding-right: 40px; padding-left: 10px; padding-top: 10px;">
                  <div
                    style="display: inline-block; width: 13px; height: 13px; background-color: #68d468; border: 1px solid grey; border-radius: 3px;">
                  </div>
                  - All trainees have been signed off by instructor
                </td>
                <td style="padding-top: 10px; padding-left: 20px;">
                  <div
                    style="display: inline-block; width: 13px; height: 13px; background-color: #f5d467; border: 1px solid grey; border-radius: 3px;">
                  </div>
                  - All trainees have signed off the task
                </td>
              </tr>
              <tr>
                <td style="padding-top: 5px; padding-left: 10px;">
                  <div
                    style="display: inline-block; width: 13px; height: 13px; background-color: #c8f5c8; border: 1px solid grey; border-radius: 3px;">
                  </div>
                  - At least one trainee has been signed off by instructor
                </td>
                <td style="padding-top: 5px; padding-left: 20px;">
                  <div
                    style="display: inline-block; width: 13px; height: 13px; background-color: #fff3cd; border: 1px solid grey; border-radius: 3px;">
                  </div>
                  - At least one trainee has signed off the task
                </td>
              </tr>
            </tbody>
          </table>
          <div class="color-filter" style="margin-top: 10px;">
            <label style="margin-right: 20px;">
              <input type="checkbox" v-model="colorFilters.allSignedOff" />
              <span class="signed-off-box">All Signed Off (Instructor)</span>
            </label>

            <label style="margin-right: 20px;">
              <input type="checkbox" v-model="colorFilters.partiallySignedOff" />
              <span class="partially-signed-off-box">Partially Signed Off</span>
            </label>

            <label style="margin-right: 20px;">
              <input type="checkbox" v-model="colorFilters.allCompleted" />
              <span class="completed-box">All Completed (Trainee)</span>
            </label>

            <label style="margin-right: 20px;">
              <input type="checkbox" v-model="colorFilters.partiallyCompleted" />
              <span class="partially-completed-box">Partially Completed</span>
            </label>
          </div>
        </div>
        <div class="button-column">
          <div class="button-group bottom-buttons-multi">
            <button @click="toggleSelectAllTasks" class="multi-sign-off-button">
              {{ isAllTasksSelected ? 'Deselect All' : 'Select All' }}
            </button>
            <button @click="openTraineeModal" class="multi-sign-off-button" :disabled="processing">
              {{ processing ? 'Processing...' : 'Sign Off Selected' }}
            </button>
          </div>
        </div>
      </div>

      <div class="container-fluid-trainee" v-if="selectedTrainees.length">
        <table class="data-table-trainee">
          <thead>
            <tr>
              <th style="width: 5%;">Chapter No
                <select v-model="selectedChapter">
                  <option value="All">All</option>
                  <option v-for="chapter in uniqueChapters" :key="chapter" :value="chapter">{{ chapter }}</option>
                </select>
              </th>
              <th style="width: 5%;">Task No</th>
              <th style="width: 5%;">MT/AT
                <select v-model="selectedMO">
                  <option value="All">All</option>
                  <option v-for="mo in uniqueMOs" :key="mo" :value="mo">{{ mo }}</option>
                </select>
              </th>
              <th style="width: 5%;">Scope
                <select v-model="selectedScope">
                  <option value="All">All</option>
                  <option v-for="scope in uniquescopes" :key="scope" :value="scope">{{ scope }}</option>
                </select>
              </th>
              <!-- Sign Off sits next to the task number so long rows are harder to mis-click -->
              <th style="width: 5%;">Sign Off
              </th>
              <th style="width: 35%;">Description</th>
              <th style="width: 15%;">Reference</th>
              <th style="width: 5%;">Skill
                <select v-model="selectedSkill">
                  <option value="All">All</option>
                  <option v-for="skill in uniqueSkills" :key="skill" :value="skill">{{ skill }}</option>
                </select>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="task in sortedFilteredTasks" :key="task.task_id" :class="{
              'signed-off-task': isTaskSignedOffInAllLogbooks(task.task_id),
              'completed-task': isTaskCompletedInAllLogbooks(task.task_id) && !isTaskSignedOffInAllLogbooks(task.task_id),
              'partially-signed-off-task': !isTaskSignedOffInAllLogbooks(task.task_id) && isTaskSignedOffInAnyLogbook(task.task_id) && !isTaskCompletedInAllLogbooks(task.task_id),
              'partially-completed-task': !isTaskCompletedInAllLogbooks(task.task_id) && isTaskCompletedInAnyLogbook(task.task_id)
            }">
              <td>{{ task.chapt_no }}</td>
              <td>{{ task.task_no }}</td>
              <td>{{ task.manob }}</td>
              <td>{{ task.scope }}</td>
              <td @click="toggleTaskCheckbox(task.task_id)" style="text-align: center;">
                <input type="checkbox" :value="task.task_id" v-model="selectedTasks"
                  :disabled="isTaskSignedOffInAllLogbooks(task.task_id) || !isTaskCompletedInAnyLogbook(task.task_id)"
                  :class="{ 'disabled-checkbox': isTaskSignedOffInAllLogbooks(task.task_id) || !isTaskCompletedInAnyLogbook(task.task_id) }"
                  @click.stop />
              </td>
              <td>
                <template v-for="(part, index) in splitReference(task.description)" :key="index">
                  {{ part }}<br v-if="index < splitReference(task.description).length - 1" />
                </template>
                <font-awesome-icon v-if="isTaskCompletedInAnyLogbook(task.task_id)" class="show-trainees-icon"
                  :icon="['fas', 'users']" @click="openCompletedTraineesModal(task)" title="Show completed trainees" />
              </td>
              <td>
                <template v-for="(part, index) in splitReference(task.reference)" :key="index">
                  {{ part }}<br v-if="index < splitReference(task.reference).length - 1" />
                </template>
              </td>
              <td>{{ task.skill }}</td>
              <!-- <td>
                <input type="checkbox" :value="task.task_id" v-model="selectedTasks"
                  :disabled="isTaskSignedOffInAllLogbooks(task.task_id) || !isTaskCompletedInAnyLogbook(task.task_id)"
                  :class="{ 'disabled-checkbox': isTaskSignedOffInAllLogbooks(task.task_id) || !isTaskCompletedInAnyLogbook(task.task_id) }" />
              </td> -->
            </tr>
            <tr v-if="sortedFilteredTasks.length === 0">
              <td colspan="100" style="text-align: center;">No Tasks have been signed off by any trainee in the group
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else class="no-trainees-message" style="margin-top: 1rem; font-style: italic; color: #888;">
        Please select a group of trainees to view and select tasks for sign off.
      </div>
    </div>
    <select-completed-trainees-modal v-if="showTraineeModal" :propTasks="modalTasks" :logbooks="logbooks"
      :initialSelected="selectedTrainees" @confirm="handleTraineeSelection" @close="closeTraineeModal" />
    <view-completed-trainees-modal v-if="showCompletedTraineesModal" :task="selectedTaskForTrainees"
      :trainees="selectedTaskForTrainees.trainees" @confirm="handleTraineeSelection"
      @close="closeCompletedTraineesModal" />
    <select-group-modal v-if="showSelectGroupModal" :propTrainees="trainees" :initialSelected="selectedTrainees"
      :projectId="projectId" :project="projects" @confirm="handleTraineesSelected" @close="closeSelectGroupModal" />
  </div>
</template>

<script>
import PageHeader from '../Examiner/Header.vue';
import axios from 'axios';
import SelectCompletedTraineesModal from './SelectCompletedTraineesModal.vue';
import SelectGroupModal from './SelectGroupModal.vue';
import ViewCompletedTraineesModal from './ViewCompletedTraineesModal.vue';
import InstructorFilter from './InstructorFilter.vue';
import BackToTop from './BackToTop.vue';

export default {
  name: 'MultiSignOff',
  data() {
    return {
      loggedInUser: '',
      logbooks: [],
      tasks: [],
      projects: [],
      traineeNames: [],
      entries: [],
      selectedTasks: [],
      modalTrainees: [],
      modalTasks: [],
      isAllTasksSelected: false,
      selectedTask: '',
      selectedProject: '',
      trainees: [],
      projectId: this.$route.params.projectId,
      filteredEntries: [],
      processing: false,
      selectedMO: 'All',
      selectedScope: 'All',
      selectedSkill: 'All',
      selectedChapter: 'All',
      showTraineeModal: false,
      showSelectGroupModal: false,
      showCompletedTraineesModal: false,
      selectedTraineesForCompleted: [],
      selectedTaskForTrainees: {},
      selectedTrainees: [],
      selectedTraineeIds: [],
      selectedInstructorIds: [],
      instructors: [],
      instructorId: parseInt(localStorage.getItem('user_id')),
      colorFilters: {
        allSignedOff: true,
        allCompleted: true,
        partiallySignedOff: true,
        partiallyCompleted: true,
      },
    };
  },
  components: {
    PageHeader,
    SelectCompletedTraineesModal,
    SelectGroupModal,
    ViewCompletedTraineesModal,
    InstructorFilter,
    BackToTop,
  },
  watch: {
    selectedInstructorIds: 'resetSelectedTasks',
    selectedChapter: 'resetSelectedTasks',
    selectedMO: 'resetSelectedTasks',
    selectedScope: 'resetSelectedTasks',
    selectedSkill: 'resetSelectedTasks',
    selectedTasks: {
      handler(newVal) {
        const targetIds = this.targetSelectableTaskIds;
        const allSelected = targetIds.length > 0 && targetIds.every(id => newVal.includes(id));
        this.isAllTasksSelected = allSelected;
      },
      deep: true,
    }
  },
  computed: {
    selectedLogbooks() {
      if (!this.selectedTrainees.length) return this.logbooks;

      const selectedIds = this.selectedTrainees.map(t => t.user_id);
      return this.logbooks.filter(lb => selectedIds.includes(lb.trainee_id));
    },
    taskEnabledMap() {
      const map = {};
      this.entries.forEach(entry => {
        if (entry.enabled) {
          const key = `${entry.task_id}_${entry.logbook_id}`;
          map[key] = true;
        }
      });
      return map;
    },
    taskCompletedMap() {
      const map = {};
      this.entries.forEach(entry => {
        if (entry.completed) {
          const key = `${entry.task_id}_${entry.logbook_id}`;
          map[key] = true;
        }
      });
      return map;
    },
    taskSignedOffMap() {
      const map = {};
      this.entries.forEach(entry => {
        if (entry.ins_so) {
          const key = `${entry.task_id}_${entry.logbook_id}`;
          map[key] = true;
        }
      });
      return map;
    },
    // task_logbook keys for tasks assigned by one of the selected instructors
    instructorAssignedMap() {
      const instructorIds = new Set(this.selectedInstructorIds);
      const map = {};
      this.entries.forEach(entry => {
        if (entry.enabled && instructorIds.has(Number(entry.ins_enabled_id))) {
          map[`${entry.task_id}_${entry.logbook_id}`] = true;
        }
      });
      return map;
    },
    // The logbooks a task's status is judged against: every selected trainee, or only
    // those whose task was assigned by one of the selected instructors
    logbooksForTask() {
      return (taskId) => {
        if (!this.selectedInstructorIds.length) return this.selectedLogbooks;
        return this.selectedLogbooks.filter(logbook =>
          this.instructorAssignedMap[`${taskId}_${logbook.logbook_id}`]
        );
      };
    },
    isTaskCompletedInAllLogbooks() {
      return (taskId) => {
        const logbooks = this.logbooksForTask(taskId);
        return logbooks.length > 0 && logbooks.every(logbook =>
          this.taskCompletedMap[`${taskId}_${logbook.logbook_id}`]
        );
      };
    },
    isTaskSignedOffInAllLogbooks() {
      return (taskId) => {
        const logbooks = this.logbooksForTask(taskId);
        return logbooks.length > 0 && logbooks.every(logbook =>
          this.taskSignedOffMap[`${taskId}_${logbook.logbook_id}`]
        );
      };
    },
    isTaskCompletedInAnyLogbook() {
      return (taskId) => {
        return this.logbooksForTask(taskId).some(logbook =>
          this.taskCompletedMap[`${taskId}_${logbook.logbook_id}`]
        );
      };
    },
    isTaskSignedOffInAnyLogbook() {
      return (taskId) => {
        return this.logbooksForTask(taskId).some(logbook =>
          this.taskSignedOffMap[`${taskId}_${logbook.logbook_id}`]
        );
      };
    },
    isTaskEnabledInAnyLogbook() {
      return (taskId) => {
        return this.logbooksForTask(taskId).some(logbook =>
          this.taskEnabledMap[`${taskId}_${logbook.logbook_id}`]
        );
      };
    },
    // Instructors who assigned at least one task to the selected trainees, with how many assignments each
    instructorOptions() {
      const logbookIds = new Set(this.selectedLogbooks.map(logbook => logbook.logbook_id));
      const counts = new Map();
      this.entries.forEach(entry => {
        if (!entry.enabled || !entry.ins_enabled_id || !logbookIds.has(entry.logbook_id)) return;
        const instructorId = Number(entry.ins_enabled_id);
        counts.set(instructorId, (counts.get(instructorId) || 0) + 1);
      });
      return Array.from(counts.entries())
        .map(([userId, count]) => {
          const instructor = this.instructors.find(i => Number(i.user_id) === userId);
          return {
            user_id: userId,
            name: instructor ? `${instructor.first_name} ${instructor.last_name}` : `Instructor #${userId}`,
            count,
          };
        })
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    targetSelectableTaskIds() {
      return this.filteredTasks
        .filter(task => this.isTaskCompletedInAnyLogbook(task.task_id) && !this.isTaskSignedOffInAllLogbooks(task.task_id))
        .map(task => task.task_id);
    },
    filteredTasks() {
      return this.tasks.filter(task => {
        const moMatches = this.selectedMO === 'All' || task.manob === this.selectedMO;
        const scopeMatches = this.selectedScope === 'All' || task.scope === this.selectedScope;
        const skillMatches = this.selectedSkill === 'All' || task.skill === this.selectedSkill;
        const chapterMatches = this.selectedChapter === 'All' || task.chapt_no === this.selectedChapter;

        const isEnabled = this.isTaskEnabledInAnyLogbook(task.task_id);
        const isCompletedInAny = this.isTaskCompletedInAnyLogbook(task.task_id);

        const allSignedOff = this.isTaskSignedOffInAllLogbooks(task.task_id);
        const allCompleted = this.isTaskCompletedInAllLogbooks(task.task_id) && !allSignedOff;

        const partiallySignedOff =
          !allSignedOff && this.isTaskSignedOffInAnyLogbook(task.task_id) && !this.isTaskCompletedInAllLogbooks(task.task_id);

        const partiallyCompleted =
          !this.isTaskCompletedInAllLogbooks(task.task_id) && this.isTaskCompletedInAnyLogbook(task.task_id) && !this.isTaskSignedOffInAnyLogbook(task.task_id);

        // Apply color filter
        const colorMatch =
          (this.colorFilters.allSignedOff && allSignedOff) ||
          (this.colorFilters.allCompleted && allCompleted) ||
          (this.colorFilters.partiallySignedOff && partiallySignedOff) ||
          (this.colorFilters.partiallyCompleted && partiallyCompleted);

        return moMatches && scopeMatches && skillMatches && chapterMatches && colorMatch && isEnabled && isCompletedInAny;
      });
    },
    sortedFilteredTasks() {
      const chapRegex = /^(\d+)([A-Za-z]*)$/;

      return this.filteredTasks.slice().sort((a, b) => {
        const [, numA, suffixA] = (a.chapt_no || '').match(chapRegex) || [];
        const [, numB, suffixB] = (b.chapt_no || '').match(chapRegex) || [];

        const chapterComparison = Number(numA) - Number(numB);
        if (chapterComparison !== 0) return chapterComparison;

        const suffixComparison = (suffixA || '').localeCompare(suffixB || '');
        if (suffixComparison !== 0) return suffixComparison;

        const taskA = parseInt(a.task_no, 10);
        const taskB = parseInt(b.task_no, 10);

        return taskA - taskB;
      });
    },
    uniqueChapters() {
      const chapters = this.tasks.map(task => task.chapt_no);
      return [...new Set(chapters)];
    },
    uniqueMOs() {
      const MOs = this.tasks.map(task => task.manob);
      return [...new Set(MOs)];
    },
    uniquescopes() {
      const scopes = this.tasks.map(task => task.scope);
      return [...new Set(scopes)];
    },
    uniqueSkills() {
      const skills = this.tasks.map(task => task.skill);
      return [...new Set(skills)];
    },
    isTraineeLocked() {
      return (traineeId) => {
        const logbook = this.logbooks.find(l => l.trainee_id === traineeId);
        return logbook ? logbook.locked === 1 : 0;
      };
    },
    hasLockedTrainee() {
      return this.selectedTrainees.some(t => this.isTraineeLocked(t.user_id));
    },
  },
  async mounted() {
    this.fetchData();
    localStorage.setItem('projectId', this.projectId);
  },
  methods: {
    goBack() {
      this.$router.push({ name: 'InstructorHome' });
    },
    toggleTaskCheckbox(taskId) {
      if (this.isTaskSignedOffInAllLogbooks(taskId)) {
        return;
      }

      const index = this.selectedTasks.indexOf(taskId);
      if (index > -1) {
        this.selectedTasks.splice(index, 1);
      } else {
        this.selectedTasks.push(taskId);
      }
    },
    splitReference(value) {
      if (!value) return [''];
      return String(value).split('\\');
    },
    removeTrainee(trainee) {
      if (confirm('This will remove the trainee from the current selection. However, this will not delete the trainee from the group permanently.\n\nDo you want to continue?')) {
        this.selectedTrainees = this.selectedTrainees.filter(t => t.user_id !== trainee.user_id);

        const selectedIds = this.selectedTrainees.map(t => t.user_id);
        this.logbooks = this.logbooks.filter(logbook =>
          selectedIds.includes(logbook.trainee_id)
        );
        const logbookIds = this.logbooks.map(logbook => logbook.logbook_id);
        this.entries = this.entries.filter(entry =>
          logbookIds.includes(entry.logbook_id)
        );

        this.selectedTasks = [];
      }
    },
    getTaskEntry(taskId) {
      return this.entries.find(entry => entry.task_id === taskId) || { initial_ac_reg: '' };
    },
    async fetchData() {
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };

        this.processing = true;
        const traineeResult = await axios.get('/api/users', { headers });
        const allTrainees = traineeResult.data.filter(user => Array.isArray(user.roles) && user.roles.includes('trainee'));
        this.instructors = traineeResult.data.filter(user => Array.isArray(user.roles) && user.roles.includes('instructor'));

        const result = await axios.get(`/api/projects/${this.projectId}/logbooks`, { headers });
        this.logbooks = result.data;

        const projectResult = await axios.get(`/api/projects/${this.projectId}`, { headers });
        this.projects = projectResult.data.project;

        const taskResult = await axios.get(`/api/tasks/products/${this.projects.product_id}`, { headers });
        this.tasks = taskResult.data;

        const entryResult = await axios.get(`api/logbookentry/projects/${this.projectId}`, { headers });
        this.entries = entryResult.data;

        const traineeIdsInLogbooks = this.logbooks.map(logbook => logbook.trainee_id);

        this.trainees = allTrainees.filter(user =>
          traineeIdsInLogbooks.includes(user.user_id)
        );

        this.filteredEntries = this.entries.filter(entry =>
          this.logbooks.some(logbook => logbook.logbook_id === entry.logbook_id)
        );

        this.selectedTasks = this.tasks
          .filter(task => this.isTaskSignedOffInAllLogbooks(task.task_id))
          .map(task => task.task_id);

        this.processing = false;
      } catch (error) {
        console.error('Error fetching data:', error);
        this.processing = false;
      }
    },
    toggleSelectAllTasks() {
      if (this.isAllTasksSelected) {

        const anyCompletedTaskIds = this.filteredTasks
          .filter(task => this.isTaskCompletedInAnyLogbook(task.task_id))
          .map(task => task.task_id);

        this.selectedTasks = this.selectedTasks.filter(
          taskId => !anyCompletedTaskIds.includes(taskId)
        );
      } else {
        const anyCompletedTaskIds = this.filteredTasks
          .filter(task => this.isTaskCompletedInAnyLogbook(task.task_id) && !this.isTaskSignedOffInAllLogbooks(task.task_id))
          .map(task => task.task_id);

        this.selectedTasks = Array.from(new Set([...this.selectedTasks, ...anyCompletedTaskIds]));
      }

      this.isAllTasksSelected = !this.isAllTasksSelected;
    },
    openTraineeModal() {
      if (this.selectedTasks.length === 0) {
        alert('Please select at least one task before enabling for trainees.');
        return;
      }

      const unsignedTasks = this.selectedTasks.filter(
        taskId => !this.isTaskSignedOffInAllLogbooks(taskId)
      );

      if (unsignedTasks.length === 0) {
        alert('All selected tasks are already signed off by the instructor.');
        return;
      }

      this.modalTasks = unsignedTasks.map(taskId => {
        const task = this.tasks.find(t => t.task_id === taskId);
        const taskEntries = this.entries.filter(entry => entry.task_id === taskId);
        const traineesForTask = this.getTraineesForTask(taskId).map(trainee => {
          const logbook = this.logbooks.find(lb => lb.trainee_id === trainee.user_id);
          const entry = taskEntries.find(e => e.logbook_id === logbook?.logbook_id) || null;

          return {
            ...trainee,
            entry
          };
        });

        return {
          ...task,
          trainees: traineesForTask
        };
      });

      console.log('modalTasks (with enriched trainees):', this.modalTasks);
      this.showTraineeModal = true;
    },
    closeTraineeModal() {
      this.showTraineeModal = false;
    },
    openCompletedTraineesModal(task) {
      const matchedTrainees = this.getTraineesForTask(task.task_id);
      const taskEntries = this.entries.filter(entry => entry.task_id === task.task_id);

      const enrichedTrainees = matchedTrainees.map(trainee => {
        const logbook = this.logbooks.find(lb => lb.trainee_id === trainee.user_id);
        if (!logbook) return { ...trainee, entry: null };

        const entry = taskEntries.find(e => e.logbook_id === logbook.logbook_id) || null;
        return {
          ...trainee,
          entry
        };
      });

      this.selectedTaskForTrainees = {
        ...task,
        trainees: enrichedTrainees
      };

      this.showCompletedTraineesModal = true;
    },
    closeCompletedTraineesModal() {
      this.showCompletedTraineesModal = false;
    },
    getTraineesForTask(taskId) {
      if (!this.entries.length || !this.logbooks.length || !this.trainees.length) {
        console.warn('Data missing: entries/logbooks/trainees not loaded');
        return [];
      }

      const logbookMap = this.logbooks.reduce((map, logbook) => {
        map[logbook.logbook_id] = logbook.trainee_id;
        return map;
      }, {});

      // With a instructor filter, only offer trainees whose task was assigned by one of those instructors
      const matchingLogbookIds = this.entries
        .filter(entry => entry.task_id === taskId && entry.enabled)
        .filter(entry => !this.selectedInstructorIds.length || this.instructorAssignedMap[`${taskId}_${entry.logbook_id}`])
        .map(entry => entry.logbook_id);

      const traineeIds = matchingLogbookIds
        .map(logbookId => logbookMap[logbookId])
        .filter(id => id !== undefined);

      const uniqueTraineeIds = [...new Set(traineeIds)];
      return this.trainees.filter(trainee =>
        uniqueTraineeIds.includes(trainee.user_id)
      );
    },
    resetSelectedTasks() {
      this.selectedTasks = this.filteredTasks
        .filter(task => this.isTaskSignedOffInAllLogbooks(task.task_id))
        .map(task => task.task_id);

      this.isAllTasksSelected = false;
    },
    async handleTraineeSelection() {
      this.showTraineeModal = false;
      this.fetchData();
    },
    openSelectGroupModal() {
      this.showSelectGroupModal = true;
    },
    closeSelectGroupModal() {
      this.showSelectGroupModal = false;
      this.fetchData();
    },
    handleTraineesSelected(selectedTraineeIds) {
      this.selectedTrainees = selectedTraineeIds;
      this.logbooks = this.logbooks.filter(logbook =>
        selectedTraineeIds.includes(logbook.trainee_id)
      );
      this.entries = this.entries.filter(entry =>
        this.logbooks.some(logbook => logbook.logbook_id === entry.logbook_id)
      );
    },
  },
}
</script>

<style scoped>
.select-project-container {
  float: left;
  /* Adjust this margin as needed */
  margin-left: 50px;
}

.select-project-container select {
  max-width: 250px;
}

span {
  border: 0px;
}

.container-fluid-trainee {
  padding-top: 10px;
  margin-left: 1%;
  margin-right: 1.5%;
}

h2 {
  font-size: 1.3rem;
  margin-bottom: 25px;
}

h1 {
  font-size: 2rem;
  margin-top: 10px;
  margin-bottom: 20px;
  padding-top: 0px;
  margin-left: 30px;
}

.completed-task {
  background-color: #a2f5a1;
  /* or any other color you want for completed tasks */
}

.signoff-buttons {
  display: flex;
  justify-content: flex-end;
  margin-right: 10px;
}

.select-task-container {
  float: left;
  /* Adjust this margin as needed */
  margin-left: 50px;
}

.select-task-container select {
  max-width: 250px;
}

.signed-off-task {
  background-color: #68d468 !important;
  /* Light green background for completed tasks */
}

.completed-task {
  background-color: #f5d467 !important;
  /* light yellow for partial */
}

.partially-completed-task {
  background-color: #fff3cd !important;
  /* light yellow for partial */
}

.partially-signed-off-task {
  background-color: #c8f5c8 !important;
  /* Light green background for completed tasks */
}

.edit-icon {
  font-size: 16px;
  color: #615f5f;
  cursor: pointer;
  transition: color 0.2s, transform 0.2s, box-shadow 0.2s;
  float: right;
  margin-right: 10px;
}

.edit-icon:hover {
  color: #2b2a2a;
  cursor: pointer;
  transform: scale(1.1);

}

.back-button {
  /* Adjust the right position as needed */
  width: 100px;
  height: 30px;
  border-radius: 5px;
  background-color: #c1e0bf;
  font-size: 14px;
  border: 2px solid black;
  padding: 3px;
  margin-right: 20px;
}

.back-button:hover {
  background-color: #abc7a9;
  cursor: pointer;
}

.ac-reg-container {
  position: relative;
}

.ac-reg-suggestions {
  position: absolute;
  background-color: white;
  border: 1px solid #ccc;
  max-height: 150px;
  overflow-y: auto;
  width: 100%;
  list-style-type: none;
  padding: 0;
  margin: 0;
  z-index: 1000;
}

.ac-reg-suggestions li {
  padding: 8px;
  cursor: pointer;
}

.ac-reg-suggestions li:hover {
  background-color: #f0f0f0;
}

.add-task-button {
  border-radius: 5px;
  min-width: 170px;
  height: 30px;
  background-color: #463333;
  color: white;
  border: 1px solid #200d0d;
  margin-top: 10px;
  margin-bottom: 10px;
  padding: 0 10px;
  margin-left: 15px;
  margin-right: 15px;
  display: inline-block;
}

.add-task-button:hover {
  background-color: #2b1616;
  color: white;
  transform: scale(1.05);
  transition: 0.3s;
}

.add-task-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.heading-button-container {
  display: flex;
  justify-content: space-between;
  margin-top: 10px;
}

.button-group {
  display: flex;
  gap: 10px;
  /* space between buttons */
}

.show-trainees-icon {
  margin-left: 10px;
  color: #007bff;
  cursor: pointer;
}

.show-trainees-icon:hover {
  color: #0056b3;
}

.artificial-fieldset {
  margin-left: 30px;
  /* Adjust margin as needed */
  width: auto;
  /* Adjust the width as necessary */
}

.artificial-fieldset {
  border: 1px solid #757575;
  border-radius: 3px;
  padding: 10px;
  margin-top: 10px;
  margin-left: 10px;
  position: relative;
}

.artificial-fieldset-legend {
  position: absolute;
  top: -10px;
  left: 10px;
  background-color: #757575;
  color: white;
  border-radius: 5px;
  padding: 0 10px;
  font-weight: bold;
  font-size: 14px;
  margin-bottom: 10px;
}

.button-column {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.button-group {
  display: flex;
  gap: 10px;
}

.bottom-buttons {
  margin-top: 20px;
}

.bottom-buttons-multi {
  margin-top: 90px;
}


.data-table-trainee {
  width: 100%;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  border-collapse: collapse;
  margin-top: 0;
  align-items: left;
  margin-bottom: 30px;
}

.disabled-checkbox {
  cursor: not-allowed;
}

.trainee-step-content {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
}

.chip-container {
  margin-top: 10px;
}

.chip {
  display: inline-block;
  padding: 5px 10px;
  margin: 5px;
  background-color: #e0f3ff;
  border-radius: 20px;
  font-size: 14px;
  position: relative;
}

.chip-close {
  margin-left: 8px;
  cursor: pointer;
  color: #888;
}

.locked-chip {
  background-color: #ffd0d0 !important;
  border: 1px solid #ff6e6e !important;
  color: #a30000 !important;
}

.lock-icon {
  margin-right: 6px;
  font-size: 14px;
}

.artificial-fieldset-steps {
  border: 1px solid #8b8b8b;
  padding: 20px 10px 10px 10px;
  margin: 20px;
  margin-bottom: 30px;
  border-radius: 2px;
}

.legend-wrapper {
  float: left;
  margin-top: -32px;
  /* Vertically overlap the border */
  margin-left: 10px;
  /* Align with the fieldset's left padding */
  background-color: #e4e4e4;
  /* Ensure background matches container */
  border: 1px solid #8b8b8b;
  border-radius: 3px;
}

.artificial-fieldset-steps-legend {
  font-size: 14px;
  font-weight: bold;
  padding: 2px 8px;
}

.color-filter label {
  cursor: pointer;
  user-select: none;
}

.color-filter input {
  margin-right: 5px;
}

.signed-off-box {
  background-color: #68d468 !important;
  border: 1px solid #54b154 !important;
  padding: 2px 6px !important;
  border-radius: 3px;
}

.partially-signed-off-box {
  background-color: #c8f5c8 !important;
  border: 1px solid #a4cfa4 !important;
  padding: 2px 6px !important;
  border-radius: 3px;
}

.completed-box {
  background-color: #f5d467 !important;
  border: 1px solid #ccac44 !important;
  padding: 2px 6px !important;
  border-radius: 3px;
}

.partially-completed-box {
  background-color: #fff3cd !important;
  border: 1px solid #ecdba1 !important;
  padding: 2px 6px !important;
  border-radius: 3px;
}

.multi-sign-off-button:disabled {
  cursor: not-allowed;
}

.page-title {
  text-align: left;
}

.project-name {
  /* display: block; */
  font-size: 20px !important;
  color: #7e828c;
}

.project-name:hover {
  color: #70747c !important;
}

.vgt-reference-column {
  white-space: normal !important;
  vertical-align: top;
}

.reference-cell {
  display: inline-block;
  line-height: 1.4;
}

/* Clips stray horizontal overflow without creating a scroll container, so sticky headers still work */
.multi-page {
  overflow-x: clip;
}

/* TraineeLogbook.css is global and also styles .data-table-trainee: its 0.5% side margins
   on top of width: 100% caused the horizontal scrollbar, and its min-width doesn't suit this page */
.data-table-trainee {
  margin-left: 0;
  margin-right: 0;
  min-width: 0;
}

/* Keep the column titles and filter dropdowns visible while scrolling */
.data-table-trainee thead {
  position: sticky;
  top: 0;
  /* Must not exceed the modal overlays' z-index (1): they come later in the page so they still cover the header */
  z-index: 1;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
}

/* With collapsed borders, a sticky header leaves its borders behind on the table grid.
   Separate borders belong to each cell, so each cell draws its right and bottom edge,
   and the first column / first header row add the outer left and top edges */
.data-table-trainee {
  border-collapse: separate;
  border-spacing: 0;
}

.data-table-trainee th,
.data-table-trainee td {
  border-width: 0 1px 1px 0;
}

.data-table-trainee tr > :first-child {
  border-left-width: 1px;
}

.data-table-trainee thead tr:first-child th {
  border-top-width: 1px;
}
</style>